import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash } from "@phosphor-icons/react";
import { api, apiError, fmtMoney } from "@/lib/api";
import { useLang } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DateField } from "@/components/DateField";

const STATUSES = ["planning", "active", "on_hold", "completed"];
const STATUS_STYLE = {
  planning: "bg-slate-100 text-slate-600",
  active: "bg-emerald-100 text-emerald-700",
  on_hold: "bg-amber-100 text-amber-700",
  completed: "bg-blue-100 text-blue-800",
};

const empty = { name: "", code: "", description: "", status: "planning", start_date: "", end_date: "", budget: 0, manager: "", area: "" };

export default function Projects() {
  const { t } = useLang();
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const canManage = ["admin", "manager"].includes(user?.role);

  const load = () => api.get("/projects").then((r) => setProjects(r.data));
  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    try {
      const payload = { ...form, budget: Number(form.budget) };
      let res;
      if (editing) res = await api.put(`/projects/${editing}`, payload);
      else res = await api.post("/projects", payload);
      const offline = res?.data?._offline;
      toast.success(offline ? t("savedOffline") : "✓");
      setOpen(false);
      setForm(empty);
      if (offline) {
        setProjects((prev) => {
          if (editing) return prev.map((x) => (x.id === editing ? { ...x, ...res.data, id: editing } : x));
          return [{ ...res.data, progress: 0, actual_cost: 0 }, ...prev];
        });
      } else {
        load();
      }
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const remove = async (project) => {
    const prompt = t("confirmDeleteProject").replace("{name}", project.name);
    if (!window.confirm(prompt)) return;
    try {
      const res = await api.delete(`/projects/${project.id}`);
      if (res?.data?._offline) {
        setProjects((p) => p.filter((x) => x.id !== project.id));
        toast.success(t("savedOffline"));
      } else {
        setProjects((p) => p.filter((x) => x.id !== project.id));
        toast.success(t("projectDeleted"));
      }
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <p className="overline-label">{t("projects")}</p>
          <h1 className="font-heading font-black text-4xl tracking-tight text-slate-900 mt-1">{t("projectPortfolio")}</h1>
        </div>
        {canManage && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-project-button" onClick={() => { setEditing(null); setForm(empty); }} className="bg-blue-900 hover:bg-blue-800 gap-2"><Plus size={16} weight="bold" /> {t("newProject")}</Button>
            </DialogTrigger>
            <DialogContent className="bg-white max-w-lg">
              <DialogHeader><DialogTitle className="font-heading">{editing ? t("edit") : t("newProject")}</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2 space-y-1.5"><Label className="overline-label">{t("name")}</Label>
                  <Input data-testid="project-name-input" value={form.name} onChange={(e) => set("name", e.target.value)} /></div>
                <div className="space-y-1.5"><Label className="overline-label">{t("code")}</Label>
                  <Input data-testid="project-code-input" value={form.code} onChange={(e) => set("code", e.target.value)} placeholder="RAM-1200" /></div>
                <div className="space-y-1.5"><Label className="overline-label">{t("status")}</Label>
                  <Select value={form.status} onValueChange={(v) => set("status", v)}>
                    <SelectTrigger data-testid="project-status-select"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white">{STATUSES.map((s) => <SelectItem key={s} value={s}>{t(s)}</SelectItem>)}</SelectContent>
                  </Select></div>
                <div className="space-y-1.5"><Label className="overline-label">{t("startDate")}</Label>
                  <DateField testid="project-start-date" value={form.start_date} onChange={(v) => set("start_date", v)} /></div>
                <div className="space-y-1.5"><Label className="overline-label">{t("endDate")}</Label>
                  <DateField testid="project-end-date" value={form.end_date} onChange={(v) => set("end_date", v)} /></div>
                <div className="space-y-1.5"><Label className="overline-label">{t("budget")} (USD)</Label>
                  <Input data-testid="project-budget-input" type="number" value={form.budget} onChange={(e) => set("budget", e.target.value)} /></div>
                <div className="space-y-1.5"><Label className="overline-label">{t("area")}</Label>
                  <Input data-testid="project-area-input" value={form.area} onChange={(e) => set("area", e.target.value)} /></div>
                <div className="space-y-1.5"><Label className="overline-label">{t("manager")}</Label>
                  <Input data-testid="project-manager-input" value={form.manager} onChange={(e) => set("manager", e.target.value)} /></div>
                <div className="col-span-2 space-y-1.5"><Label className="overline-label">{t("description")}</Label>
                  <Textarea data-testid="project-description-input" value={form.description} onChange={(e) => set("description", e.target.value)} /></div>
              </div>
              <Button data-testid="project-save-button" onClick={submit} disabled={!form.name || !form.code || !form.start_date || !form.end_date} className="bg-blue-900 hover:bg-blue-800 w-full">{editing ? t("update") : t("create")}</Button>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-lg">
        <table className="w-full data-table">
          <thead>
            <tr><th>{t("code")}</th><th>{t("name")}</th><th>{t("area")}</th><th>{t("status")}</th><th>{t("progress")}</th><th className="text-right">{t("budget")}</th><th className="text-right">{t("actualCost")}</th>{canManage && <th></th>}</tr>
          </thead>
          <tbody data-testid="projects-table-body">
            {projects.map((p) => (
              <tr key={p.id}>
                <td className="font-mono text-xs font-semibold">{p.code}</td>
                <td><Link to={`/projects/${p.id}`} data-testid={`project-link-${p.code}`} className="font-medium text-blue-900 hover:underline">{p.name}</Link></td>
                <td className="text-slate-500">{p.area}</td>
                <td><span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded ${STATUS_STYLE[p.status]}`}>{t(p.status)}</span></td>
                <td>
                  <div className="flex items-center gap-2">
                    <div className="h-1.5 w-20 bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full bg-blue-900" style={{ width: `${p.progress}%` }} />
                    </div>
                    <span className="text-xs text-slate-500">{p.progress}%</span>
                  </div>
                </td>
                <td className="text-right font-medium">{fmtMoney(p.budget)}</td>
                <td className="text-right font-medium text-amber-600">{fmtMoney(p.actual_cost)}</td>
                {canManage && (
                  <td className="whitespace-nowrap">
                    <button data-testid={`project-edit-${p.code}`} className="text-slate-400 hover:text-blue-900 transition-colors mr-3"
                      onClick={() => {
                        setEditing(p.id);
                        setForm({ name: p.name, code: p.code, description: p.description, status: p.status, start_date: p.start_date, end_date: p.end_date, budget: p.budget, manager: p.manager, area: p.area });
                        setOpen(true);
                      }}><PencilSimple size={16} /></button>
                    <button data-testid={`project-delete-${p.code}`} className="text-slate-400 hover:text-red-500 transition-colors"
                      onClick={() => remove(p)}><Trash size={16} /></button>
                  </td>
                )}
              </tr>
            ))}
            {!projects.length && <tr><td colSpan={8} className="text-center text-slate-400 py-10">{t("noData")}</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
